import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin( ScrollTrigger, ScrollToPlugin );

// スマホ実機では、スクロール中にアドレスバーの表示/非表示でビューポートの高さだけが
// 変化するタイミングがある。デフォルトのScrollTriggerはこの高さ変化でも自動的に
// refresh()（全トリガーのstart/end位置の再計算）を行ってしまい、スクロール中に
// 再計算が走ることで start/end 位置がずれ、#mv-pre-register の追従表示トリガー
// （bottom top）が正しく発火しなくなる。PCのブラウザでスマホ表示をエミュレートした
// 場合はビューポート高さが変化しないため再現せず、実機でだけ発生していた。
// 高さのみの変化（モバイルのアドレスバー開閉）ではrefreshしないよう設定する。
ScrollTrigger.config( { ignoreMobileResize: true } );

const techgalaSectionTriggers = {};
const mm = gsap.matchMedia();

const historySec = document.querySelector( '.sec-our-history' );
const contactBottomSec = document.querySelector( '.sec-contact-bottom' );

/**
 * ScrollTriggerを更新
 */
function techgala_refreshScrollAnimation() {
	ScrollTrigger.refresh();
}

/**
 * カードを1個ずつ表示するアニメーション
 */
gsap.utils.toArray( '.wp-block-group.is-style-card-grid > *' ).forEach( ( cardGrid ) => {
	gsap.from( cardGrid, {
		opacity: 0,
		y: 50,
		scrollTrigger: {
			trigger: cardGrid,
			start: 'top bottom+=50',
			end: '+=50',
			toggleActions: 'play none none reverse',
			invalidateOnRefresh: true,
		},
		duration: 1,
	} );
} );

/**
 * 角丸（is-style-round-corners / is-style-round-corners-top）セクションが
 * 前のセクションにかぶさりながら現れるスクロールアニメーション。
 *
 * 動き方:
 *   1. 要素(prev)の下端が画面下に来るまでは何もしない（通常のスクロール）。
 *   2. prevの下端が画面下に来たらprevを固定（pin）する。
 *   3. 次の要素(curr)の上端が画面上に来たらprevの固定を解除する。
 *   4. これをセクションの並び順で繰り返す。
 *
 * これはPC/SPどちらでも既存のprogram→ambassador等のピン処理
 * （trigger: prev / start: 'bottom bottom' / endTrigger: curr / end: 'top top'）
 * と同じ仕組みで、curr側に特別な transform は不要（通常のドキュメントの並び順で
 * prevより手前に来るため、固定されたprevの上にcurrが自然にかぶさって見える）。
 *
 * 以前はabout→event-overview間だけの専用実装だったが、
 * about（sec-about-techgala）セクションがTOPページから削除されたため、
 * 「角丸セクションが前の要素にかぶさる」という見た目を、
 * .entry-content直下の角丸セクション全てに汎用化して適用する。
 *
 * ※ sec-our-history → sec-contact-bottom 間は、下の「ContactBottom」ブロックで
 *   専用のピンアニメーションを行っているため、ここでは対象外にする。
 * ※ MV（sec-mv）は対象外にする。MVは `min-height: 768px` の固定高さ
 *   （@see src/scss/top/_mv.scss）で、ビューポートの高さがそれより大きい環境では
 *   「prevの下端が画面下に来たら固定する」という開始条件がページ読み込み時点で
 *   既に成立してしまい、MVがピン開始位置（画面上部が空くずれた位置）に
 *   固定されたまま動かなくなる不具合があったため、MVを prev として使わないように
 *   対象（sections）から除外する。
 */
function techgala_setupSectionOverlaps() {
	const container = document.querySelector( '.entry-content' );
	if ( ! container ) {
		return;
	}

	const sections = Array.from( container.children ).filter( ( el ) => {
		if ( ! ( el instanceof HTMLElement ) ) {
			return false;
		}
		if ( el.classList.contains( 'pin-spacer' ) ) {
			return false;
		}
		if ( el.classList.contains( 'micromodal-modal' ) ) {
			return false;
		}
		if ( el.classList.contains( 'sec-mv' ) ) {
			return false;
		}
		if ( getComputedStyle( el ).display === 'none' ) {
			return false;
		}
		return true;
	} );

	sections.forEach( ( curr, i ) => {
		if ( i === 0 ) {
			return;
		}

		const isRoundStyle =
			curr.classList.contains( 'is-style-round-corners-top' ) ||
			curr.classList.contains( 'is-style-round-corners' ) ||
			curr.classList.contains( 'is-style-round-corners-bottom' );

		if ( ! isRoundStyle ) {
			return;
		}

		if ( curr.classList.contains( 'sec-contact-bottom' ) ) {
			return;
		}

		const prev = sections[ i - 1 ];

		techgalaSectionTriggers[ `overlap${ i }` ] = ScrollTrigger.create( {
			trigger: prev,
			start: 'bottom bottom',
			endTrigger: curr,
			end: 'top top',
			pin: true,
			pinSpacing: false,
			anticipatePin: 0,
			invalidateOnRefresh: true,
			scrub: true,
		} );
	} );
}

techgala_setupSectionOverlaps();


/**
 * MV内の #mv-pre-register / #mv-sponsorship に閉じるボタンを追加する。
 * ボタンはこのJSで動的に生成・挿入する。
 *
 * クリック時の挙動:
 *   - PCの #mv-pre-register / #mv-sponsorship、SPの #mv-sponsorship は、
 *     従来どおりボックス自体を非表示にする（box.style.display = 'none'）。
 *   - SPの #mv-pre-register だけは例外。SPでは画面下部への追従表示
 *     （is-pinned。@see 下の「#mv-pre-register のSP追従表示」/ src/scss/top/_mv.scss）
 *     という「追従バナー」としての見た目を閉じたいだけで、MVセクション内の
 *     通常表示まで消してしまうと、MVまでスクロールで戻ってきても
 *     二度と表示されなくなってしまう。そのため、ボックス自体は非表示にせず
 *     is-follow-dismissed クラスを付与するだけにする。CSS側で
 *     「is-pinned かつ is-follow-dismissed」の場合のみ非表示にしているため、
 *     追従表示中に閉じればその場で消え、MV内の通常表示（is-pinnedではない状態）
 *     には影響しない。以降は（onEnter側のガードにより）再度追従表示にもならない。
 */
( function () {
	const closableBoxes = document.querySelectorAll( '#mv-pre-register, #mv-sponsorship' );

	closableBoxes.forEach( ( box ) => {
		const closeButton = document.createElement( 'button' );
		closeButton.type = 'button';
		closeButton.className = 'mv-box-close';
		closeButton.setAttribute( 'aria-label', '閉じる' );

		closeButton.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			event.stopPropagation();

			const isSpMvPreRegisterFollow =
				box.id === 'mv-pre-register' && window.matchMedia( '(max-width: 1019px)' ).matches;

			if ( isSpMvPreRegisterFollow ) {
				box.classList.add( 'is-follow-dismissed' );
				return;
			}

			box.style.display = 'none';
		} );

		box.appendChild( closeButton );
	} );
} )();


/**
 * #mv-pre-register のSP追従表示
 *
 * SP（max-width: 1019px）でのみ、MV（.sec-mv）セクション全体がビューポート上端より
 * 上へスクロールし終えたタイミングで #mv-pre-register を画面下部に固定表示し、
 * 常に追従させる（見た目・サイズは通常表示時のまま。CSS側は is-pinned 付与時に
 * position関連のみ上書きする。@see src/scss/top/_mv.scss）。
 * 逆方向にスクロールしてMVセクションが再度ビューポートに入ってきたら固定を解除し、
 * MV内の通常の表示位置に戻す。
 *
 * #mv-sponsorship は対象外（現状の表示・挙動を維持する）。MVセクションが完全に
 * 画面外へ出た後に固定へ切り替えるため、#mv-pre-register がレイアウトから
 * 抜けても #mv-sponsorship の表示位置には影響しない。
 */
( function () {
	const mvSection = document.querySelector( '.sec-mv' );
	const preRegister = document.querySelector( '#mv-pre-register' );
	if ( ! mvSection || ! preRegister ) {
		return;
	}

	// #mv-pre-register の祖先である .sec-mv（wp-block-cover）には、カバー画像の
	// 角丸・トリミングのために overflow: clip が指定されている。仕様上は
	// position: fixed の要素は祖先の overflow: hidden/clip があってもビューポート
	// 基準で外側にはみ出して表示されるはずだが、Safari（WebKit）はこのケースで
	// fixed要素を正しく「脱出」させずにクリッピングしてしまう既知の挙動差がある
	// （Chromeでは問題なく表示される。実機・PC版Safari双方で確認済み）。
	// #mv-pre-register が追従表示（is-pinned）になるのは .sec-mv が画面から
	// 完全にスクロールアウトした後だけなので、そのタイミングに限り .sec-mv 自身の
	// overflow を visible に切り替えても、.sec-mv側の見た目（カバー画像の
	// トリミングなど）には一切影響しない。
	mm.add( '(max-width: 1019px)', () => {
		techgalaSectionTriggers.mvPreRegisterPin = ScrollTrigger.create( {
			trigger: mvSection,
			start: 'bottom top',
			onEnter: () => {
				if ( ! preRegister.classList.contains( 'is-follow-dismissed' ) ) {
					mvSection.classList.add( 'mv-pre-register-pinned' );
					preRegister.classList.add( 'is-pinned' );
				}
			},
			onLeaveBack: () => {
				preRegister.classList.remove( 'is-pinned' );
				mvSection.classList.remove( 'mv-pre-register-pinned' );
			},
		} );

		return () => {
			techgalaSectionTriggers.mvPreRegisterPin.kill();
			delete techgalaSectionTriggers.mvPreRegisterPin;
			preRegister.classList.remove( 'is-pinned' );
			mvSection.classList.remove( 'mv-pre-register-pinned' );
		};
	} );
} )();


/**
 * ContactBottom
 */
if ( contactBottomSec && historySec ) {
	mm.add( '(min-width: 1020px)', () => {
		techgalaSectionTriggers.historySec = ScrollTrigger.create( {
			trigger: historySec,
			start: 'bottom bottom',
			endTrigger: contactBottomSec,
			end: 'top top+=80',
			pin: true,
			pinSpacing: false,
			scrub: true,
		} );
		return () => {
			techgalaSectionTriggers.historySec.kill();
			delete techgalaSectionTriggers.historySec;
		};
	} );
	mm.add( '(max-width: 1019px)', () => {
		techgalaSectionTriggers.historySec = ScrollTrigger.create( {
			trigger: historySec,
			start: 'bottom bottom',
			endTrigger: contactBottomSec,
			end: 'top top',
			pin: true,
			pinSpacing: false,
			scrub: true,
		} );
		return () => {
			techgalaSectionTriggers.historySec.kill();
			delete techgalaSectionTriggers.historySec;
		};
	} );
}


/**
 * 初期化後に高さ更新・別ページからのセクション遷移対応
 */

window.addEventListener( 'load', () => {
	techgala_refreshScrollAnimation();

	const pendingHash = sessionStorage.getItem( 'techgala_scrollTo' );
	if ( pendingHash ) {
		sessionStorage.removeItem( 'techgala_scrollTo' );
		const target = document.querySelector( pendingHash );
		if ( target ) {
			techgala_scrollToTarget( target );
		}
	}
} );

/**
 * リサイズ時にScrollTriggerを更新
 */
let techgala_resizeTimer;
let techgala_isDesktop = window.innerWidth >= 1020;

window.addEventListener( 'resize', () => {
    clearTimeout( techgala_resizeTimer );
    techgala_resizeTimer = setTimeout( () => {
        const isDesktop = window.innerWidth >= 1020;
        if ( isDesktop !== techgala_isDesktop ) {
					window.location.reload();
					return;
        }
				techgala_refreshScrollAnimation();
    }, 250 );
} );
/**
 * ページ内スクロール共通処理
 */
function techgala_scrollToTarget( target ) {
	const getAbsoluteTop = ( el ) => {
		let top = 0;
		while ( el ) {
			top += el.offsetTop;
			el = el.offsetParent;
		}
		return top;
	};
	let offset = 20;
	if ( window.innerWidth >= 1020 ) {
		offset = 60;
	}
	const targetY = getAbsoluteTop( target ) - offset;
	gsap.to( window, {
		duration: 1,
		ease: 'power2.out',
		scrollTo: {
			y: targetY,
			autoKill: false,
		},
	} );
}

/**
 * ページ内リンク
 */
document.querySelectorAll( 'a[href^="#"]' ).forEach( ( link ) => {
	link.addEventListener( 'click', ( event ) => {
		const hash = link.getAttribute( 'href' );

		if ( ! hash || '#' === hash ) {
			return;
		}

		const target = document.querySelector( hash );
		if ( ! target ) {
			return;
		}

		event.preventDefault();

		const handleRefresh = () => {
			ScrollTrigger.removeEventListener( 'refresh', handleRefresh );
			techgala_scrollToTarget( target );
		};
		ScrollTrigger.addEventListener( 'refresh', handleRefresh );
		ScrollTrigger.refresh();

		const navModal = document.querySelector(
			'.wp-block-navigation__responsive-container'
		);

		if ( ! navModal ) {
			return;
		}

		const closeButton = navModal.querySelector(
			'.wp-block-navigation-overlay-close'
		);

		if ( closeButton ) {
			closeButton.click();
		}
	} );
} );
